import { formatPesoAmount } from '../../utils/money';
import { normalizePriceRuleSelection } from '../../utils/complexPriceRules';
import { normalizeFormatPromotions } from '../../utils/priceListPromotions';
import { applySharedRetailConfiguration } from '../../utils/sharedRetailConfiguration';

const normalizeText = (value) => String(value || '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .trim()
  .toLowerCase();

const isPresent = (value) => value !== null && value !== undefined && value !== '';

const normalizeProductFormatCode = (value) => String(value || '').trim().toLowerCase();

// Product format codes are generated from names with lowercase letters, numbers, and hyphens.
// Keeping the aggregate option outside that alphabet lets a real format named `all` remain
// independently selectable.
export const ALL_PRODUCT_FORMATS_FILTER = '__all_formats__';

const firstDefined = (...values) => values.find(
  (value) => value !== undefined && value !== null
);

export const isCurrentQuickPriceFormat = (format) => format?.productFormatActive !== false;

const compareQuickPriceFormats = (left, right) => {
  const leftOrder = Number(left?.productFormatSortOrder);
  const rightOrder = Number(right?.productFormatSortOrder);
  const leftHasOrder = left?.productFormatSortOrder !== null
    && left?.productFormatSortOrder !== undefined
    && Number.isFinite(leftOrder);
  const rightHasOrder = right?.productFormatSortOrder !== null
    && right?.productFormatSortOrder !== undefined
    && Number.isFinite(rightOrder);
  if (leftHasOrder && rightHasOrder && leftOrder !== rightOrder) return leftOrder - rightOrder;
  if (leftHasOrder !== rightHasOrder) return leftHasOrder ? -1 : 1;
  const nameCompare = String(left?.productFormatName || '')
    .localeCompare(String(right?.productFormatName || ''), 'es', { sensitivity: 'base' });
  if (nameCompare !== 0) return nameCompare;
  const codeCompare = String(left?.productFormatCode || '')
    .localeCompare(String(right?.productFormatCode || ''), 'es', { sensitivity: 'base' });
  if (codeCompare !== 0) return codeCompare;
  return String(left?.id ?? '').localeCompare(String(right?.id ?? ''), undefined, {
    numeric: true,
  });
};

const resolveProductFormatIdentity = (identity = {}) => {
  const productFormatId = identity.productFormatId
    ?? identity.productFormat?.id
    ?? identity.ProductFormat?.id
    ?? null;
  const productFormatCode = normalizeProductFormatCode(
    identity.productFormatCode
      || identity.productFormat?.code
      || identity.ProductFormat?.code
      || identity.clientType
  );
  const configuredName = String(
    identity.productFormatName
      || identity.productFormat?.name
      || identity.ProductFormat?.name
      || ''
  ).trim();
  const productFormatName = configuredName || productFormatCode || 'Formato';
  const rawProductFormatActive = firstDefined(
    identity.productFormatActive,
    identity.productFormat?.isActive,
    identity.ProductFormat?.isActive
  );
  const productFormatActive = rawProductFormatActive === true
    ? true
    : rawProductFormatActive === false
      ? false
      : null;
  const rawProductFormatSortOrder = firstDefined(
    identity.productFormatSortOrder,
    identity.productFormat?.sortOrder,
    identity.ProductFormat?.sortOrder
  );
  const parsedProductFormatSortOrder = Number(rawProductFormatSortOrder);
  const productFormatSortOrder = rawProductFormatSortOrder !== undefined
    && rawProductFormatSortOrder !== null
    && Number.isFinite(parsedProductFormatSortOrder)
    ? parsedProductFormatSortOrder
    : null;
  return {
    productFormatId,
    productFormatCode,
    productFormatName,
    productFormatActive,
    productFormatSortOrder,
  };
};

export const normalizeQuickPriceCatalog = (response) => (
  Array.isArray(response?.data) ? response.data : []
).map((product) => ({
  id: product.id,
  name: product.productName || 'Producto sin nombre',
  image: product.productImage || null,
  category: product.category || null,
  pricingBasis: product.pricing?.effectivePricingBasis || 'unit',
  varieties: (Array.isArray(product.varieties) ? product.varieties : []).map((variety) => ({
    id: variety.id,
    name: variety.varietyName || 'Variedad sin nombre',
    image: variety.varietyImage || null,
    inventoryBaseUnit: variety.inventoryBaseUnit || 'kg',
    formats: applySharedRetailConfiguration((Array.isArray(variety.formats) ? variety.formats : []).map((format) => {
      const identity = format.identity || {};
      const shared = format.shared || {};
      const branch = format.branch || {};
      const {
        productFormatId,
        productFormatCode,
        productFormatName,
        productFormatActive,
        productFormatSortOrder,
      } = resolveProductFormatIdentity(identity);
      const priceRule = normalizePriceRuleSelection(
        Object.hasOwn(branch, 'priceRule')
          ? branch.priceRule
          : format.analysis?.priceRule?.selection,
        branch.stockPriceRule
      );
      return {
        ...identity,
        completion: format.completion || null,
        id: format.varietyPriceId,
        productFormatId,
        productFormatCode,
        productFormatName,
        productFormatActive,
        productFormatSortOrder,
        // Retain these aliases while callers migrate from the old client-type naming.
        clientType: productFormatCode,
        clientTypeLabel: productFormatName,
        shared,
        sellingPrice: branch.sellingPrice ?? null,
        effectiveSellingPrice: branch.effectiveSellingPrice ?? branch.sellingPrice ?? null,
        isAvailable: Boolean(branch.isAvailable),
        offerAvailability: branch.offerAvailability || null,
        isConfigured: Boolean(branch.isConfigured),
        isPriceLocked: Boolean(branch.isPriceLocked),
        priceRule,
        priceRuleAssignment: branch.priceRuleAssignment || null,
        stockPriceRule: branch.stockPriceRule
          || (priceRule?.type === 'simple' ? priceRule.rule : null),
        formatOffer: format.formatOffer !== undefined
          ? format.formatOffer
          : branch.formatOffer || null,
        promotions: normalizeFormatPromotions({
          promotions: Array.isArray(format.promotions)
            ? format.promotions
            : branch.promotions,
        }),
        costs: format.costs || null,
        analysis: format.analysis && typeof format.analysis === 'object' ? format.analysis : null,
      };
    })),
  })),
}));

export const getProductFormats = (product) => (
  Array.isArray(product?.varieties)
    ? product.varieties.flatMap((variety) => variety.formats || [])
    : []
);

export const getCurrentProduct = (product) => ({
  ...product,
  varieties: (Array.isArray(product?.varieties) ? product.varieties : []).flatMap((variety) => {
    const formats = (Array.isArray(variety?.formats) ? variety.formats : [])
      .filter(isCurrentQuickPriceFormat)
      .sort(compareQuickPriceFormats);
    return formats.length ? [{ ...variety, formats }] : [];
  }),
});

export const getConfiguredFormats = (product) => (
  getProductFormats(product).filter((format) => format.isConfigured && format.sellingPrice !== null)
);

export const isSelectableQuickPriceFormat = (format) => (
  format?.id !== null && format?.id !== undefined
    && isCurrentQuickPriceFormat(format)
    && !format.isPriceLocked
);

export const getSelectableCatalogFormatIds = (products) => (
  (Array.isArray(products) ? products : []).flatMap((product) => (
    getProductFormats(product).flatMap((format) => (
      isSelectableQuickPriceFormat(format)
        ? [format.id]
        : []
    ))
  ))
);

export const getPriceLockSummary = (formats) => {
  const currentFormats = (Array.isArray(formats) ? formats : [])
    .filter(isCurrentQuickPriceFormat);
  const lockedCount = currentFormats.filter((format) => format.isPriceLocked).length;
  const totalCount = currentFormats.length;
  return {
    state: totalCount > 0 && lockedCount === totalCount
      ? 'locked'
      : lockedCount > 0
        ? 'partial'
        : 'unlocked',
    lockedCount,
    unlockedCount: totalCount - lockedCount,
    totalCount,
  };
};

export const getQuickPriceFormatOptions = (catalog) => {
  const optionsByCode = new Map();
  (Array.isArray(catalog) ? catalog : []).forEach((product) => {
    (product.varieties || []).forEach((variety) => {
      (variety.formats || []).forEach((format) => {
        const value = normalizeProductFormatCode(
          format.productFormatCode || format.clientType
        );
        if (!value) return;
        const label = String(
          format.productFormatName || format.clientTypeLabel || value
        ).trim() || value;
        const current = optionsByCode.get(value);
        const productFormatActive = format.productFormatActive === true
          ? true
          : format.productFormatActive === false
            ? false
            : null;
        const parsedSortOrder = Number(format.productFormatSortOrder);
        const productFormatSortOrder = format.productFormatSortOrder !== null
          && format.productFormatSortOrder !== undefined
          && Number.isFinite(parsedSortOrder)
          ? parsedSortOrder
          : null;
        if (!current) {
          optionsByCode.set(value, {
            value,
            label,
            productFormatId: format.productFormatId ?? null,
            productFormatActive,
            productFormatSortOrder,
          });
          return;
        }
        optionsByCode.set(value, {
          ...current,
          ...(current.label === value && label !== value ? { label } : {}),
          ...(current.productFormatId === null && format.productFormatId !== null
            && format.productFormatId !== undefined
            ? { productFormatId: format.productFormatId }
            : {}),
          productFormatActive: current.productFormatActive === false
            || productFormatActive === false
            ? false
            : current.productFormatActive === true || productFormatActive === true
              ? true
              : null,
          productFormatSortOrder: current.productFormatSortOrder
            ?? productFormatSortOrder,
        });
      });
    });
  });
  return Array.from(optionsByCode.values())
    .filter((option) => option.productFormatActive !== false)
    .sort((left, right) => {
      const leftHasSortOrder = Number.isFinite(left.productFormatSortOrder);
      const rightHasSortOrder = Number.isFinite(right.productFormatSortOrder);
      if (leftHasSortOrder && rightHasSortOrder
        && left.productFormatSortOrder !== right.productFormatSortOrder) {
        return left.productFormatSortOrder - right.productFormatSortOrder;
      }
      if (leftHasSortOrder !== rightHasSortOrder) return leftHasSortOrder ? -1 : 1;
      return left.label.localeCompare(right.label, 'es', { sensitivity: 'base' })
        || left.value.localeCompare(right.value);
    });
};

export const getAvailabilitySummary = (formats, { product = false } = {}) => {
  const configured = (Array.isArray(formats) ? formats : [])
    .filter((format) => format.isConfigured);
  if (!configured.length) {
    return {
      label: 'Sin configurar',
      tone: 'neutral',
      isAvailable: false,
      configuredCount: 0,
    };
  }
  const availableCount = configured.filter((format) => format.isAvailable).length;
  if (availableCount === configured.length) {
    return {
      label: 'Disponible',
      tone: 'available',
      isAvailable: true,
      configuredCount: configured.length,
    };
  }
  if (availableCount === 0) {
    return {
      label: product ? 'No disponible' : 'Agotada',
      tone: 'unavailable',
      isAvailable: false,
      configuredCount: configured.length,
    };
  }
  return {
    label: 'Parcial',
    tone: 'partial',
    isAvailable: true,
    configuredCount: configured.length,
  };
};

export const updateFormatInCatalog = (catalog, formatId, updates) => (
  (Array.isArray(catalog) ? catalog : []).map((product) => ({
    ...product,
    varieties: product.varieties.map((variety) => ({
      ...variety,
      formats: variety.formats.map((format) => (
        String(format.id) === String(formatId)
          ? { ...format, ...updates }
          : format
      )),
    })),
  }))
);

export const updateProductAvailabilityInCatalog = (catalog, productId, isAvailable, formatIds) => {
  const targetIds = formatIds ? new Set(formatIds.map(String)) : null;
  return (Array.isArray(catalog) ? catalog : []).map((product) => (
    String(product.id) !== String(productId)
      ? product
      : {
          ...product,
          varieties: product.varieties.map((variety) => ({
            ...variety,
            formats: variety.formats.map((format) => (
              format.isConfigured && isCurrentQuickPriceFormat(format)
                && (!targetIds || targetIds.has(String(format.id)))
                ? { ...format, isAvailable,
                  ...(format.offerAvailability && format.isAvailable !== isAvailable ? {
                    offerAvailability: isAvailable ? null : {
                      ...format.offerAvailability,
                      endedAt: format.offerAvailability.endedAt || new Date().toISOString(),
                      endedReason: format.offerAvailability.endedReason || 'manual',
                    },
                  } : {}),
                }
                : format
            )),
          })),
        }
  ));
};

export const filterQuickPriceCatalog = (
  catalog,
  {
    query = '',
    formatCode,
    clientType,
    categoryId = 'all',
    targetFormatId = '',
  } = {}
) => {
  const normalizedQuery = normalizeText(query);
  const normalizedFormatCode = normalizeProductFormatCode(
    formatCode ?? clientType ?? ALL_PRODUCT_FORMATS_FILTER
  );
  const normalizedCategoryId = String(categoryId || 'all');
  const targetId = String(targetFormatId || '');

  return (Array.isArray(catalog) ? catalog : []).flatMap((product) => {
    if (normalizedCategoryId !== 'all'
      && String(product.category?.id ?? 'uncategorized') !== normalizedCategoryId) {
      return [];
    }
    const varieties = product.varieties.flatMap((variety) => {
      const formats = variety.formats.filter((format) => {
        if (format.productFormatActive === false) return false;
        if (targetId && String(format.id) !== targetId) return false;
        if (normalizedFormatCode !== ALL_PRODUCT_FORMATS_FILTER
          && normalizeProductFormatCode(format.productFormatCode || format.clientType)
          !== normalizedFormatCode) return false;
        if (!normalizedQuery) return true;
        return [
          product.name,
          variety.name,
          format.productFormatName,
          format.productFormatCode,
          format.clientTypeLabel,
          format.description,
          format.barcode,
          format.metricUnit,
        ].some((value) => normalizeText(value).includes(normalizedQuery));
      }).sort(compareQuickPriceFormats);
      return formats.length ? [{ ...variety, formats }] : [];
    });
    return varieties.length ? [{ ...product, varieties }] : [];
  });
};

export const formatMoney = (value) => {
  if (!isPresent(value)) return 'Sin dato';
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 'Sin dato';
  return `$${formatPesoAmount(Math.round(parsed))}`;
};

export const getActiveManualCost = (format) => {
  const costs = format?.costs;
  // A newer purchase supersedes the stored estimate without deleting it.
  // Keep compatibility with servers that do not yet expose cost-event metadata.
  if (costs && Object.hasOwn(costs, 'manualCostUpdatedAt') && costs.costSource === 'stock_list') {
    return null;
  }
  return format?.shared?.manualBaseCost ?? null;
};

const hasStockRowId = (row) => {
  const stockId = Number(row?.stockId);
  return Number.isSafeInteger(stockId) && stockId > 0;
};

const getStockRowOrder = (row = {}, index = 0) => {
  const createdAt = new Date(
    row.createdAt ?? row.stockCreatedAt ?? row.timestamp ?? null
  ).getTime();
  const numericId = Number(row.stockId);
  return {
    createdAt: Number.isFinite(createdAt) ? createdAt : 0,
    numericId: Number.isSafeInteger(numericId) ? numericId : 0,
    stringId: String(row.stockId ?? ''),
    index,
  };
};

// `editableSourceRow` is authoritative on current servers. Diagnostic `rows` are
// retained as a compatibility source for catalog responses produced before that
// field was added. Match the server's newest-row ordering whenever timestamps are
// available, with the stock id (and finally response order) as deterministic ties.
export const getEditableStandardStockCostRow = (breakdown = {}) => {
  if (breakdown?.listType !== 'standard') return null;
  if (Object.hasOwn(breakdown, 'editableSourceRow')) {
    return hasStockRowId(breakdown.editableSourceRow)
      ? breakdown.editableSourceRow
      : null;
  }

  return (Array.isArray(breakdown?.rows) ? breakdown.rows : []).reduce(
    (latest, candidate, index) => {
      if (!hasStockRowId(candidate)) return latest;
      if (!latest) return { row: candidate, order: getStockRowOrder(candidate, index) };
      const order = getStockRowOrder(candidate, index);
      if (order.createdAt !== latest.order.createdAt) {
        return order.createdAt > latest.order.createdAt ? { row: candidate, order } : latest;
      }
      if (order.numericId !== latest.order.numericId) {
        return order.numericId > latest.order.numericId ? { row: candidate, order } : latest;
      }
      const idComparison = order.stringId.localeCompare(
        latest.order.stringId,
        undefined,
        { numeric: true }
      );
      if (idComparison !== 0) return idComparison > 0 ? { row: candidate, order } : latest;
      return order.index > latest.order.index ? { row: candidate, order } : latest;
    },
    null
  )?.row || null;
};

export const getEditableStandardStockCostSource = (costs = {}) => {
  const breakdown = costs?.referenceCostBreakdown;
  if (breakdown?.listType !== 'standard') return null;
  const sourceStockListId = Number(
    breakdown.sourceStockListId ?? costs?.sourceStockListId
  );
  if (!Number.isSafeInteger(sourceStockListId) || sourceStockListId <= 0) return null;
  const editableSourceRow = getEditableStandardStockCostRow(breakdown);
  if (!editableSourceRow) return null;
  return { breakdown, editableSourceRow, sourceStockListId };
};

// An unusable conversion or a newer manual estimate can still have a purchase
// source. Do not offer a second purchase solely because its cost is unavailable.
export const hasStockPurchaseSource = (costs = {}) => {
  const value = costs || {};
  const breakdown = value.referenceCostBreakdown || {};
  return Boolean(value.sourceStockListId || breakdown.sourceStockListId
    || value.costComponents?.base?.sourceStockListId
    || value.costComponents?.operational?.sourceStockListId
    || breakdown.editableSourceRow?.stockId
    || breakdown.rows?.some((row) => row.stockId)
    || Number(breakdown.rowCount) > 0
    || value.costSource === 'stock_list' || value.referenceCostSource === 'stock_list');
};

export const getEffectiveCost = (format) => {
  const costs = format?.costs || {};
  const analyzedReferenceCost = format?.analysis?.referenceCost?.saleUnitValue;
  const base = costs.costComponents?.base || {};
  const operational = costs.costComponents?.operational || {};
  const baseAmount = base.available && isPresent(base.perSaleUnit)
    ? Number(base.perSaleUnit)
    : null;
  const operationalAmount = operational.available && isPresent(operational.perSaleUnit)
    ? Number(operational.perSaleUnit)
    : null;
  const direct = isPresent(analyzedReferenceCost)
    ? Number(analyzedReferenceCost)
    : isPresent(costs.referenceCost) ? Number(costs.referenceCost) : null;
  const combined = baseAmount !== null
    ? baseAmount + (operationalAmount || 0)
    : null;
  const effective = Number.isFinite(direct)
    ? direct
    : Number.isFinite(combined) ? combined : null;
  return {
    effective,
    base: Number.isFinite(baseAmount) ? baseAmount : null,
    operational: Number.isFinite(operationalAmount) ? operationalAmount : null,
    source: base.sourceStockListName || costs.sourceStockListName || null,
    status: costs.conversionStatus || base.conversionStatus || null,
  };
};

const RULE_MODE_LABELS = {
  markup_amount: 'Sumar',
  markup_percent: 'Sumar',
  fixed_price: 'Precio fijo',
};

export const formatActiveRule = (rule) => {
  if (!rule || typeof rule !== 'object') return '';
  const mode = String(rule.mode || '');
  const value = Number(rule.value);
  const basis = rule.basis === 'kg' ? 'por kg' : 'por unidad';
  const label = RULE_MODE_LABELS[mode] || 'Regla automática';
  if (!Number.isFinite(value)) return `${label} · ${basis}`;
  if (mode === 'markup_percent') return `${label} ${value.toLocaleString('es-CL')}% · ${basis}`;
  if (mode === 'markup_amount') return `${label} ${formatMoney(value)} · ${basis}`;
  return `${label}: ${formatMoney(value)} · ${basis}`;
};

export const extractPolicyWarning = (error) => {
  const data = error?.response?.data || {};
  if (Number(error?.response?.status) !== 409
    || data.code !== 'pricing_policy_confirmation_required') {
    return null;
  }
  const warnings = (Array.isArray(data.warnings) ? data.warnings : []).flatMap((warning) => {
    if (typeof warning === 'string') return [{ message: warning }];
    if (!warning || typeof warning !== 'object') return [];
    return [{
      ...warning,
      message: warning.message || warning.label || warning.code || 'Este precio requiere confirmación.',
    }];
  });
  return {
    warnings,
    analysis: data.analysis && typeof data.analysis === 'object' ? data.analysis : {},
    branchAnalyses: Array.isArray(data.branchAnalyses) ? data.branchAnalyses : [],
    ...(typeof data.policyWarningToken === 'string'
      ? { policyWarningToken: data.policyWarningToken }
      : {}),
  };
};

export const getPricingAnalysisRows = (analysis = {}) => {
  const candidates = [
    ['sellingPrice', 'Precio solicitado', analysis.sellingPrice],
    ['referenceCost', 'Costo efectivo', analysis.referenceCost?.saleUnitValue],
    ['recommendedSellingPrice', 'Precio recomendado', analysis.recommendedSellingPrice],
  ];
  const seen = new Set();
  return candidates.flatMap(([key, label, value]) => {
    if (!isPresent(value)) return [];
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) return [];
    const signature = `${label}:${parsed}`;
    if (seen.has(signature)) return [];
    seen.add(signature);
    return [{ key, label, value: parsed }];
  });
};
